/* Libraries */
import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import Button from '~/src/components/Button';
import qs from 'qs';

/* Containers */
import LibraryContainer from './Library';
import TemplatesContainer from '~/src/containers/Templates';
import TemplateSetsContainer from '~/src/containers/TemplateSets';
import CourtFormsTab from '~/src/components/CourtForms/CourtFormsTab';
import RegionalCourtFormContainer from '~/src/containers/RegionalCourtFormContainer/RegionalCourtFormContainer';
import PdfTableContainer from '~/src/containers/PdfTableContainer';
import StateRegionTableContainer from '~/src/containers/StateRegionTableContainer';

import { denormalizeLocality } from '~/src/components/CourtForms/StateRegionTable';
import FederalFormsWithLocalitiesContainer, {
  GENERAL_FEDERAL_FORMS_CATEGORY,
} from '~/src/containers/FederalFormsWithLocalitiesContainer';
import TemplateDefaultValuesListContainer from '~/src/containers/TemplateDefaultValuesList';
import Page from '~/src/components/PageLayout';
import Tabs from '~/src/components/PageLayout/Tabs';
import { history } from '~/src/utils/history';
import { SelectionSidebarHeader } from '~/src/components/SelectionSidebar/SelectionSidebarHeader';
import { SaveAsSetButton } from './SaveAsSetButton';
import {
  useCreateClioQuestionnaire,
  useCreateQuestionnaireTemplate,
} from '~/src/entities/questionnaires';
import {
  useCurrentOrgFprint,
  useDraftingAgentEnabled,
} from '~/src/entities/user';
import { regional } from '~/src/regions/translations/regionalTranslations';

// Hooks
import useFeatureAccesses from '~/src/hooks/useFeatureAccesses';
import useShowCourtFormsTabs from '~/src/hooks/useShowCourtFormsTabs';

export const COURT_FORMS_ROUTE = '/library/court-forms';

import { TemplateSource } from '~/src/types/template';

export function getTemplateIdFromSidebarItem(item) {
  return item.globalTemplateId || item.templateId || item.id;
}

export function selectionContainsFormTemplate(items) {
  return items.some((i) => i.defaultValueId != null);
}

export const LibraryPage = () => {
  const isDraftingAgentEnabled = useDraftingAgentEnabled();
  const featureAccesses = useFeatureAccesses();
  const showCourtFormsTabs = useShowCourtFormsTabs();

  const orgFprint = useCurrentOrgFprint();
  const createClioQuestionnaire = useCreateClioQuestionnaire(orgFprint);
  const createQuestionnaireTemplate = useCreateQuestionnaireTemplate();

  const handleCreateQuestionnaire = async (items) => {
    const templateIds = items.map(getTemplateIdFromSidebarItem);

    const questionnaire = await createClioQuestionnaire.mutateAsync({
      clioQuestionnaire: {
        title: 'Untitled Questionnaire',
        description: '',
        introduction: '',
        conclusion: '',
        target_count: 0,
      },
    });

    await createQuestionnaireTemplate.mutateAsync({
      orgFprint,
      questionnaireId: questionnaire.id,
      templateIds,
    });

    history.push(`/questionnaires/${questionnaire.id}/auto-generate`);
  };

  const tabs = (
    <Tabs
      tabsMetadata={[
        {
          headerText: 'Sets',
          toUrl: '/library/template-sets',
        },
        {
          headerText: regional('courtForms'),
          toUrl: COURT_FORMS_ROUTE,
          show: showCourtFormsTabs,
        },
        {
          headerText: 'Form templates',
          toUrl: '/library/form-templates',
          show: showCourtFormsTabs,
        },
        {
          headerText: 'Word templates',
          toUrl: '/library/templates',
        },
      ]}
    />
  );

  return (
    <Page
      showSelectionSidebar
      selectionSidebarProps={{
        title: 'Selection',
        renderHeader: (items) => (
          <SelectionSidebarHeader
            title={'Selection'}
            count={items.length}
            extra={<SaveAsSetButton items={items} />}
            showDraftingSpecialistSection={isDraftingAgentEnabled}
          />
        ),
        renderFooter: (items) => (
          <div
            className="border-t p-3 flex justify-end"
            style={{ gap: '16px' }}
          >
            {isDraftingAgentEnabled &&
              (() => {
                const hasFormTemplate = selectionContainsFormTemplate(items);
                return (
                  <div data-tip data-for="create-questionnaire-button">
                    <Button
                      disabled={items.length === 0 || hasFormTemplate}
                      onClick={() => handleCreateQuestionnaire(items)}
                    >
                      Create questionnaire
                    </Button>
                    <ReactTooltip
                      id="create-questionnaire-button"
                      place="bottom"
                      effect="solid"
                      getContent={() =>
                        hasFormTemplate
                          ? "Form templates aren't supported in questionnaires yet. Remove them from your selection to continue."
                          : null
                      }
                    />
                  </div>
                );
              })()}
            <Button
              primary
              disabled={items.length === 0}
              onClick={() => {
                const templateIds = items.map(getTemplateIdFromSidebarItem);
                const queryParams = qs.stringify(
                  { ids: templateIds },
                  { encode: false },
                );
                history.push(`/templates/docset/new?${queryParams}`);
              }}
            >
              {isDraftingAgentEnabled
                ? 'Draft'
                : `Draft new ${items.length < 2 ? 'document' : 'documents'} →`}
            </Button>
          </div>
        ),
      }}
      title={'Library'}
      headerProps={{ tabs }}
    >
      <LibraryContainer>
        <Switch>
          <Route
            exact
            path="/library"
            render={({ location }) => {
              // If organization has purchased word document automation, redirect to word templates tab
              // Otherwise, redirect to template sets (original behavior)
              const defaultPath =
                featureAccesses.isWordDocumentAutomationEnabled
                  ? '/library/templates'
                  : '/library/template-sets';

              return (
                <Redirect
                  to={{
                    pathname: defaultPath,
                    search: location.search,
                  }}
                />
              );
            }}
          />

          <Route exact path="/library/template-sets">
            <TemplateSetsContainer />
          </Route>

          <Route exact path={COURT_FORMS_ROUTE}>
            <CourtFormsTab />
          </Route>
          <Route
            path={`${COURT_FORMS_ROUTE}/additional-region-forms/:regionCode`}
            render={({ match: { url, params } }) => (
              <RegionalCourtFormContainer
                routeUrl={url}
                regionFilter={params.regionCode}
                courtFormsRootUrl={COURT_FORMS_ROUTE}
              />
            )}
          />
          <Route exact path="/library/form-templates">
            <TemplateDefaultValuesListContainer />
          </Route>
          <Route exact path={`${COURT_FORMS_ROUTE}/federal_USCIS`}>
            {featureAccesses.isFederalFormsCategoriesEnabled ? (
              <FederalFormsWithLocalitiesContainer
                routeUrl={COURT_FORMS_ROUTE}
              />
            ) : (
              <PdfTableContainer filterCategory="FEDERAL" />
            )}
          </Route>
          <Route
            exact
            path={`${COURT_FORMS_ROUTE}/:stateCode/form-listing`}
            render={({ match: { params } }) => {
              const { stateCode } = params;
              return (
                <PdfTableContainer filterCategory={stateCode} locality="None" />
              );
            }}
          />
          <Route
            exact
            path={`${COURT_FORMS_ROUTE}/:stateCode/locality-listing`}
            render={({ match: { params } }) => {
              const { stateCode } = params;
              return <StateRegionTableContainer filterCategory={stateCode} />;
            }}
          />
          <Route
            exact
            path={`${COURT_FORMS_ROUTE}/:stateCode/:locality/form-listing`}
            render={({ match: { params } }) => {
              const { stateCode, locality: normalizedLocality } = params;
              const denormalizedLocality =
                denormalizeLocality(normalizedLocality);
              const effectiveLocality =
                denormalizedLocality === GENERAL_FEDERAL_FORMS_CATEGORY
                  ? 'None'
                  : denormalizedLocality;
              return (
                <PdfTableContainer
                  filterCategory={stateCode}
                  locality={effectiveLocality}
                />
              );
            }}
          />

          <Route exact path="/library/templates">
            <TemplatesContainer enableClioWorkCopy />
          </Route>
          <Route exact path="/library/templates/clio">
            <TemplatesContainer source={TemplateSource.Clio} />
          </Route>
        </Switch>
      </LibraryContainer>
    </Page>
  );
};
